import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, FileText } from 'lucide-react';

const ProjectBasics = ({ onSubmit, initialData = {} }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    projectName: initialData.projectName || '',
    periodFrom: initialData.periodFrom || '',
    periodTo: initialData.periodTo || '',
    ...initialData
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.projectName.trim()) {
      newErrors.projectName = t('validation.projectName.required');
    }

    if (!formData.periodFrom) {
      newErrors.periodFrom = t('validation.periodFrom.required');
    }

    if (!formData.periodTo) {
      newErrors.periodTo = t('validation.periodTo.required');
    }

    // Validate that periodTo is not before periodFrom
    if (formData.periodFrom && formData.periodTo) {
      const fromDate = new Date(formData.periodFrom);
      const toDate = new Date(formData.periodTo);
      
      if (toDate < fromDate) {
        newErrors.periodTo = t('validation.periodTo.mustBeAfterFrom');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('workflow.scopeBoundaries.projectBasics.title')}
              </h2>
              <p className="text-gray-600 mt-1">
                {t('workflow.scopeBoundaries.projectBasics.description')}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Project Name */}
          <div>
            <label htmlFor="projectName" className="block text-sm font-medium text-gray-700 mb-2">
              {t('workflow.scopeBoundaries.projectBasics.projectName')} *
            </label>
            <input
              type="text"
              id="projectName"
              value={formData.projectName}
              onChange={(e) => handleInputChange('projectName', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.projectName ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder={t('workflow.scopeBoundaries.projectBasics.projectNamePlaceholder')}
            />
            {errors.projectName && (
              <p className="mt-1 text-sm text-red-600">{errors.projectName}</p>
            )}
          </div>

          {/* Reporting Period */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {t('workflow.scopeBoundaries.projectBasics.reportingPeriod')} *
            </label>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Period From */}
              <div>
                <label htmlFor="periodFrom" className="block text-sm font-medium text-gray-600 mb-2">
                  {t('workflow.scopeBoundaries.projectBasics.periodFrom')}
                </label>
                <div className="relative">
                  <input
                    type="date"
                    id="periodFrom"
                    value={formData.periodFrom}
                    onChange={(e) => handleInputChange('periodFrom', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.periodFrom ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  <Calendar className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
                </div>
                {errors.periodFrom && (
                  <p className="mt-1 text-sm text-red-600">{errors.periodFrom}</p>
                )}
              </div>

              {/* Period To */}
              <div>
                <label htmlFor="periodTo" className="block text-sm font-medium text-gray-600 mb-2">
                  {t('workflow.scopeBoundaries.projectBasics.periodTo')}
                </label>
                <div className="relative">
                  <input
                    type="date"
                    id="periodTo"
                    value={formData.periodTo}
                    onChange={(e) => handleInputChange('periodTo', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.periodTo ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  <Calendar className="absolute right-3 top-2.5 h-5 w-5 text-gray-400 pointer-events-none" />
                </div>
                {errors.periodTo && (
                  <p className="mt-1 text-sm text-red-600">{errors.periodTo}</p>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-6 border-t border-gray-200">
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              {t('common.continue')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectBasics;
